<script setup lang="ts">
const props = withDefaults(defineProps<{
  modelValue?: boolean
  closeOnContentClick?: boolean
  openOnHover?: boolean
  openDelay?: number
  closeDelay?: number
  location?: string
  origin?: string
  minWidth?: string | number
  maxWidth?: string | number
  maxHeight?: string | number
  contentClass?: string
  fullWidth?: boolean
}>(), {
  closeOnContentClick: true,
  openDelay: 0,
  closeDelay: 0,
})

const emit = defineEmits<{
  'update:modelValue': [val: boolean]
}>()

const isOpen = ref(props.modelValue ?? false)
const menuRef = ref<HTMLElement>()
const activatorRef = ref<HTMLElement>()
const contentRef = ref<HTMLElement>()
let openTimer: ReturnType<typeof setTimeout> | null = null
let closeTimer: ReturnType<typeof setTimeout> | null = null

watch(() => props.modelValue, v => { if (v !== undefined) isOpen.value = v })
watch(isOpen, v => emit('update:modelValue', v))

function clearTimers() {
  if (openTimer) { clearTimeout(openTimer); openTimer = null }
  if (closeTimer) { clearTimeout(closeTimer); closeTimer = null }
}

function open() {
  clearTimers()
  if (props.openDelay > 0) {
    openTimer = setTimeout(() => { isOpen.value = true }, props.openDelay)
  } else {
    isOpen.value = true
  }
}

function close() {
  clearTimers()
  if (props.closeDelay > 0) {
    closeTimer = setTimeout(() => { isOpen.value = false }, props.closeDelay)
  } else {
    isOpen.value = false
  }
}

function toggle() { isOpen.value ? close() : open() }

function onContentClick() {
  if (props.closeOnContentClick) {
    clearTimers()
    isOpen.value = false
  }
}

function onMouseEnter() {
  if (props.openOnHover) open()
}

function onMouseLeave() {
  if (props.openOnHover) close()
}

function onContentMouseEnter() {
  if (props.openOnHover) clearTimers()
}

function onContentMouseLeave() {
  if (props.openOnHover) close()
}

function onClickOutside(e: MouseEvent) {
  const target = e.target as Node
  const inMenu = menuRef.value?.contains(target)
  const inContent = contentRef.value?.contains(target)
  if (!inMenu && !inContent) {
    clearTimers()
    isOpen.value = false
  }
}

onMounted(() => document.addEventListener('click', onClickOutside, true))
onUnmounted(() => {
  document.removeEventListener('click', onClickOutside, true)
  clearTimers()
})

const activatorProps = computed(() => ({
  onClick: toggle,
}))

const contentPosition = ref({ top: '0px', left: '0px' })

function updateDropdownPosition() {
  if (props.fullWidth || !activatorRef.value) return
  const rect = activatorRef.value.getBoundingClientRect()
  const loc = props.location || 'bottom start'
  let top = rect.bottom + 4
  let left = rect.left

  if (loc.includes('end')) {
    left = rect.right
  }

  if (left + 200 > window.innerWidth) {
    left = rect.right - 200
  }
  if (top + 300 > window.innerHeight) {
    top = rect.top - 4
  }

  contentPosition.value = { top: `${top}px`, left: `${left}px` }
}

const fullWidthTop = ref('0px')

function updateFullWidthPosition() {
  if (!props.fullWidth || !activatorRef.value) return
  const parent = activatorRef.value.closest('.m-app-bar, .compact-header, header')
  const top = parent
    ? parent.getBoundingClientRect().bottom
    : activatorRef.value.getBoundingClientRect().bottom
  fullWidthTop.value = `${top}px`
}

watch(isOpen, async (open) => {
  if (!open) return
  await nextTick()
  if (props.fullWidth) {
    updateFullWidthPosition()
  } else {
    updateDropdownPosition()
  }
})

const contentStyles = computed(() => {
  const s: Record<string, string> = {}
  if (props.minWidth) s.minWidth = typeof props.minWidth === 'number' ? `${props.minWidth}px` : props.minWidth
  if (props.maxWidth) s.maxWidth = typeof props.maxWidth === 'number' ? `${props.maxWidth}px` : props.maxWidth
  if (props.maxHeight) s.maxHeight = typeof props.maxHeight === 'number' ? `${props.maxHeight}px` : props.maxHeight
  return s
})
</script>

<template>
  <div
    ref="menuRef"
    class="m-menu"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <div ref="activatorRef" class="m-menu__activator">
      <slot name="activator" :props="activatorProps" :is-active="isOpen" />
    </div>

    <ClientOnly>
      <Teleport to="body">
        <!-- Normal dropdown -->
        <Transition v-if="!fullWidth" name="m-menu-drop">
          <div
            v-if="isOpen"
            ref="contentRef"
            class="m-menu__content"
            :class="contentClass"
            :style="{ ...contentStyles, top: contentPosition.top, left: contentPosition.left }"
            @click="onContentClick"
            @mouseenter.stop="onContentMouseEnter"
            @mouseleave.stop="onContentMouseLeave"
          >
            <slot />
          </div>
        </Transition>

        <!-- Full-width megamenu -->
        <Transition v-else name="m-mega">
          <div
            v-if="isOpen"
            ref="contentRef"
            class="m-menu__fullwidth"
            :class="contentClass"
            :style="{ top: fullWidthTop }"
            @click="onContentClick"
            @mouseenter.stop="onContentMouseEnter"
            @mouseleave.stop="onContentMouseLeave"
          >
            <slot />
          </div>
        </Transition>
      </Teleport>
    </ClientOnly>
  </div>
</template>

<style>
.m-menu {
  position: relative;
  display: inline-flex;
}

.m-menu__activator {
  display: inline-flex;
}

.m-menu__content {
  position: fixed;
  z-index: var(--m-z-dropdown);
  background: var(--m-surface);
  border: 1px solid var(--m-border);
  border-radius: var(--m-radius);
  box-shadow: var(--m-shadow-lg);
  min-width: 180px;
  padding: var(--m-space-1) 0;
}

.m-menu-drop-enter-active,
.m-menu-drop-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.m-menu-drop-enter-from,
.m-menu-drop-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.m-menu__fullwidth {
  position: fixed;
  left: 0;
  right: 0;
  z-index: var(--m-z-dropdown);
  display: flex;
  justify-content: center;
}

.m-mega-enter-active {
  transition: opacity 0.25s ease, transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.m-mega-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.m-mega-enter-from {
  opacity: 0;
  transform: translateY(-12px);
}
.m-mega-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
